/**
 * THE APPROVAL RECORD: one round of sign-off, as it happened, read-only.
 *
 * The product was storing all of this and showing almost none of it. The
 * acceptance review put it plainly: a surveyor asking "which chain did this
 * follow, why that one, what sign-offs did it require, and show me that the
 * committee met on the 14th" could not be answered from any screen, though
 * every fact was already in Firestore and, for most of them, already in the
 * browser and dropped by a narrow TypeScript interface.
 *
 * NO SECOND SOURCE OF TRUTH. Everything here comes from
 * `getApprovalInstanceFn`, which has always returned the instance, its
 * decisions, and the CHAIN AS COPIED AT SUBMISSION — and which, until now, no
 * page called. Nothing is recomputed from the workflow as it reads today: that
 * is the whole point of the copied chain, and it is why this record still
 * answers the question after the workflow has been edited, renamed or retired.
 */

import { formatDay } from '../../shared/dates';
import { useQuery } from '@tanstack/react-query';
import { call } from '../../lib/callable';
import { isCallFailure } from '../../lib/callable';
import { stepSummary } from '../approvals/stepText';
import type { ApprovalStep } from '../approvals/approvalsData';
import { toMoment, DECISION_LABEL, type HistoryDecision } from './PolicyHistory';
import styles from '../../shared/Page.module.css';
import own from './PolicyHistory.module.css';

/** The chain a step of this approval was copied from, plus what it was set to. */
type ChainStep = ApprovalStep & { stepOrder: number };

interface Routing {
  workflowId?: string;
  workflowName?: string;
  level?: 'type_and_department' | 'type' | 'department' | 'catch_all' | 'chosen';
  reason?: string;
  documentTypeName?: string | null;
  departmentName?: string | null;
  chosen?: { defaultWorkflowId?: string | null; defaultWorkflowName?: string | null; byUid?: string; byName?: string | null; reason?: string };
}

export interface RecordInstance {
  instanceId: string;
  status: string;
  workflowId?: string;
  workflowVersion?: number | null;
  routing?: Routing;
  currentStepOrder?: number;
  artifactTitle?: string;
  artifactNumber?: string | null;
  artifactVersionNumber?: number | null;
  changeSummary?: string | null;
  submittedByName?: string | null;
  submittedByEmail?: string | null;
  startedAt?: unknown;
  completedAt?: unknown;
  withdrawal?: { byName?: string | null; byEmail?: string | null; reason?: string; stepName?: string | null; at?: unknown } | null;
  cancellation?: { cause?: string; at?: unknown; byName?: string | null } | null;
}

/** What routing decided, in the words a person reads rather than the enum. */
export const ROUTING_LEVEL: Record<string, string> = {
  type_and_department: 'Matched the document type and the department',
  type: 'Matched the document type',
  department: 'Matched the department',
  catch_all: 'Nothing covered it, so it followed the catch-all',
  chosen: 'Chosen by hand, in place of the routed workflow',
};

/** Why the system closed an approval nobody withdrew. */
export const CANCELLATION_CAUSE: Record<string, string> = {
  superseded: 'Replaced when the document was submitted again',
  published_directly: 'Ended because the document was published directly',
  submission_rolled_back: 'The submission was rolled back',
  policy_retired: 'Ended because the document was retired',
};

/**
 * What became of this round. A cancelled approval is NOT a withdrawal unless
 * somebody actually withdrew it: four system paths close one, and calling all
 * of them "Withdrawn" asserted a human act that never happened.
 */
export function outcomeOf(inst: Pick<RecordInstance, 'status' | 'withdrawal' | 'cancellation'>): { label: string; tone: 'ok' | 'info' | 'neutral' | 'danger'; detail: string | null } {
  switch (inst.status) {
    case 'approved': return { label: 'Approved', tone: 'ok', detail: 'Every step was satisfied, and the last approval published the document or held it for its effective date.' };
    case 'rejected': return { label: 'Returned to the author', tone: 'danger', detail: 'A decision sent it back. The document returned to draft with the reason below.' };
    case 'cancelled':
      if (inst.withdrawal) return { label: 'Withdrawn', tone: 'neutral', detail: null };
      if (inst.cancellation?.cause) return { label: 'Cancelled', tone: 'neutral', detail: CANCELLATION_CAUSE[inst.cancellation.cause] ?? 'Closed by StandingDocs.' };
      return { label: 'Cancelled', tone: 'neutral', detail: 'Reason not recorded. This approval was closed before StandingDocs began recording why.' };
    default: return { label: 'In progress', tone: 'info', detail: null };
  }
}

function Fact({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className={styles.factTerm}>{term}</dt>
      <dd className={styles.factValue}>{children}</dd>
    </div>
  );
}

const absent = <span className={styles.absent}>&mdash;</span>;

export function useApprovalRecord(instanceId: string | null) {
  return useQuery({
    queryKey: ['approval-record', instanceId],
    enabled: Boolean(instanceId),
    queryFn: () => call<{ instanceId: string }, {
      instance: RecordInstance; decisions: HistoryDecision[]; steps: ChainStep[];
      /** What the organization withholds from this reader. */
      withheld?: { comments?: boolean; committeeEvidence?: boolean };
    }>('getApprovalInstanceFn', { instanceId: instanceId! }),
  });
}

export function ApprovalRecord({ instanceId }: { instanceId: string }) {
  const { data, isLoading, error } = useApprovalRecord(instanceId);

  if (isLoading) return <p className={own.muted}>Loading the approval record…</p>;
  if (error) {
    return (
      <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
        The approval record could not be loaded, so this page cannot say what happened.
        {isCallFailure(error) || error instanceof Error ? ` ${error.message}` : ''}
      </div>
    );
  }
  if (!data) return null;

  const { instance, decisions, steps } = data;
  const withheld = data.withheld ?? {};
  const routing = instance.routing ?? {};
  const outcome = outcomeOf(instance);
  const chosen = routing.level === 'chosen' ? routing.chosen : undefined;
  // Steps that share a stage number were asked at the same time.
  const stages = [...new Set(steps.map((s) => s.stepOrder))].sort((a, b) => a - b);

  return (
    <div className={own.record} role="group" aria-label="Approval record">
      <section aria-labelledby={`rec-doc-${instanceId}`}>
        <h4 className={own.recordTitle} id={`rec-doc-${instanceId}`}>The document</h4>
        <dl className={styles.facts}>
          <Fact term="Title">{instance.artifactTitle || absent}</Fact>
          <Fact term="Number">{instance.artifactNumber || absent}</Fact>
          <Fact term="Version">{instance.artifactVersionNumber != null ? `Version ${instance.artifactVersionNumber}` : absent}</Fact>
          <Fact term="Submitted by">{instance.submittedByName || instance.submittedByEmail || absent}</Fact>
          <Fact term="Submitted"><span className="tnum">{toMoment(instance.startedAt) ?? '\u2014'}</span></Fact>
        </dl>
        {instance.changeSummary && <p className={own.summary}><strong>What changed:</strong> {instance.changeSummary}</p>}
      </section>

      <section aria-labelledby={`rec-wf-${instanceId}`}>
        <h4 className={own.recordTitle} id={`rec-wf-${instanceId}`}>The workflow it followed</h4>
        <dl className={styles.facts}>
          <Fact term="Workflow">{routing.workflowName || absent}</Fact>
          <Fact term="Workflow version">{instance.workflowVersion != null ? `Version ${instance.workflowVersion}` : <span className={styles.absent}>Not recorded</span>}</Fact>
          <Fact term="Why this one">{routing.level ? ROUTING_LEVEL[routing.level] ?? routing.level : absent}</Fact>
          <Fact term="Document type">{routing.documentTypeName || absent}</Fact>
          <Fact term="Department">{routing.departmentName || absent}</Fact>
        </dl>
        {routing.reason && <p className={own.summary}>{routing.reason}</p>}
        {chosen && (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="note">
            <strong>Chosen by hand.</strong> Routing gave{' '}
            {chosen.defaultWorkflowName ? <>&ldquo;{chosen.defaultWorkflowName}&rdquo;</> : 'another workflow'}.{' '}
            {chosen.byName ?? 'Somebody'} sent it through &ldquo;{routing.workflowName}&rdquo; instead
            {chosen.reason ? <>, because: &ldquo;{chosen.reason}&rdquo;</> : ''}.
          </div>
        )}
      </section>

      <section aria-labelledby={`rec-chain-${instanceId}`}>
        <h4 className={own.recordTitle} id={`rec-chain-${instanceId}`}>The sign-offs it required</h4>
        <p className={own.muted}>
          The chain as it stood when the document was submitted. Later changes to the workflow did not reach this
          approval, so this is what was actually required.
        </p>
        {steps.length === 0 ? <p className={own.muted}>No chain was recorded for this approval.</p> : (
          <ol className={own.chain} aria-label="The chain as it stood at submission">
            {stages.map((stage) => {
              const together = steps.filter((s) => s.stepOrder === stage);
              return (
                <li key={stage} className={own.stage}>
                  <span className={own.stageNo} aria-hidden="true">{stage}</span>
                  <div>
                    {together.length > 1 && <p className={own.muted}>These {together.length} were asked at the same time.</p>}
                    {together.map((s) => (
                      <div key={s.stepId} className={own.chainStep}>
                        <span className={own.chainName}><span className="vh">Stage {stage}: </span>{s.name}</span>
                        <span className={own.muted}>{stepSummary(s, []).join(' · ')}</span>
                      </div>
                    ))}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <section aria-labelledby={`rec-dec-${instanceId}`}>
        <h4 className={own.recordTitle} id={`rec-dec-${instanceId}`}>The decisions taken</h4>
        {/*
          * The fields are absent from the payload, not blanked on screen, so
          * there is nothing here to reveal. This says so, because a decision
          * with no reason beside it otherwise reads as a lost record.
          */}
        {withheld.comments && decisions.length > 0 && (
          <p className={own.muted}>
            Your organization does not show the comments written during approval to your role.
          </p>
        )}
        {withheld.committeeEvidence && decisions.length > 0 && (
          <p className={own.muted}>
            Your organization does not show committee meeting details to your role.
          </p>
        )}
        {decisions.length === 0 ? <p className={own.muted}>No decision was recorded on this approval.</p> : (
          <ul className={own.decisions} aria-label="Decisions, in the order they were taken">
            {decisions.map((d) => (
              <li key={d.decisionId}>
                <span className={own.decisionWhat}>{DECISION_LABEL[d.decisionType]}</span>
                {' '}at {d.stepName ?? `step ${d.stepOrder}`} by {d.userName ?? d.userEmail ?? 'an approver'}
                {' '}on <span className="tnum">{toMoment(d.decidedAt) ?? 'a date not recorded'}</span>
                {d.comments && <div className={own.comment}>&ldquo;{d.comments}&rdquo;</div>}
                {d.committee && (
                  <div className={own.committee}>
                    <strong>Committee decision.</strong> {d.userName ?? d.userEmail ?? 'The person above'} recorded what the
                    committee resolved; they did not make the decision themselves.
                    <dl className={styles.facts} style={{ marginTop: 'var(--sp-2)' }}>
                      {/* A day, stored as the bare text the form collected, so
                          it is timezone-proof already. It only ever needed the
                          formatting every other date on the record uses. */}
                      <Fact term="Committee met"><span className="tnum">{formatDay(d.committee.meetingDate)}</span></Fact>
                      <Fact term="Vote">{d.committee.vote || <span className={styles.absent}>Not recorded</span>}</Fact>
                      <Fact term="Minutes">{d.committee.minutesRef || <span className={styles.absent}>Not recorded</span>}</Fact>
                    </dl>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby={`rec-out-${instanceId}`}>
        <h4 className={own.recordTitle} id={`rec-out-${instanceId}`}>What became of it</h4>
        <p>
          <span className={`${styles.tag} ${styles[outcome.tone]}`}>{outcome.label}</span>
          {Boolean(instance.completedAt) && <span className={own.muted}> · closed {toMoment(instance.completedAt)}</span>}
          {instance.status !== 'approved' && instance.status !== 'rejected' && instance.status !== 'cancelled'
            && <span className={own.muted}> · waiting at stage {instance.currentStepOrder ?? 1}</span>}
        </p>
        {outcome.detail && <p className={own.muted}>{outcome.detail}</p>}
        {instance.withdrawal && (
          <dl className={styles.facts}>
            <Fact term="Withdrawn by">{instance.withdrawal.byName || instance.withdrawal.byEmail || absent}</Fact>
            <Fact term="Withdrawn"><span className="tnum">{toMoment(instance.withdrawal.at) ?? '\u2014'}</span></Fact>
            <Fact term="Waiting at">{instance.withdrawal.stepName || absent}</Fact>
            <Fact term="Reason">{instance.withdrawal.reason || absent}</Fact>
          </dl>
        )}
        {!instance.withdrawal && instance.cancellation && Boolean(instance.cancellation.at) && (
          <p className={own.muted}>
            Closed {toMoment(instance.cancellation.at)}
            {instance.cancellation.byName ? ` after ${instance.cancellation.byName} acted on the document` : ''}.
          </p>
        )}
      </section>
    </div>
  );
}
